import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Camera, Loader2, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FRAMES = 10;
const GAP_MS = 280;
const LEAD_MS = 700;
const stop = (s) => s?.getTracks().forEach((t) => t.stop());

const grab = (v, maxEdge, q) => {
  const k = Math.min(1, maxEdge / Math.max(v.videoWidth, v.videoHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(v.videoWidth * k); c.height = Math.round(v.videoHeight * k);
  c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', q);
};

const TurnOverlay = ({ turn, testId }) => {
  const Arrow = turn.direction === 'left' ? ArrowLeft : ArrowRight;
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/35 text-white" data-testid={`${testId}-turn`}>
      <Arrow className="w-20 h-20 text-amber-300 animate-pulse" />
      <div className="text-2xl font-bold text-center px-4" data-testid={`${testId}-turn-dir`}>Slowly turn your head to your {turn.direction.toUpperCase()}</div>
      <div className="w-2/3 h-2 mt-4 rounded-full bg-white/30 overflow-hidden"><div className="h-full bg-amber-400 transition-[width] duration-200" style={{ width: `${(turn.n / FRAMES) * 100}%` }} /></div>
    </div>
  );
};

/** Live selfie; with liveness it then records a short head-turn (left/right at random) for the server check. */
export const LiveSelfieCamera = ({ liveness, facing = 'user', onDone, onCancel, testId }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timers = useRef([]);
  const [state, setState] = useState('starting');
  const [attempt, setAttempt] = useState(0);
  const [turn, setTurn] = useState(null);

  useEffect(() => {
    let alive = true;
    setState('starting');
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setState('unavailable'); return; }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 } } });
        if (!alive) { stop(s); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
        setState('live');
      } catch (e) {
        if (alive) setState(['NotAllowedError', 'SecurityError', 'PermissionDeniedError'].includes(e?.name) ? 'blocked' : 'unavailable');
      }
    })();
    const t = timers.current;
    return () => { alive = false; stop(streamRef.current); streamRef.current = null; t.forEach(clearTimeout); };
  }, [attempt, facing]);

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const selfie = grab(v, 720, 0.75);
    if (!liveness) { stop(streamRef.current); onDone(selfie, null); return; }
    const direction = Math.random() < 0.5 ? 'left' : 'right';
    const frames = [];
    setTurn({ direction, n: 0 });
    for (let i = 0; i < FRAMES; i += 1) {
      timers.current.push(setTimeout(() => {
        const vv = videoRef.current;
        if (vv?.videoWidth) frames.push(grab(vv, 480, 0.6));
        setTurn({ direction, n: i + 1 });
        if (i === FRAMES - 1) { stop(streamRef.current); onDone(selfie, { direction, frames }); }
      }, LEAD_MS + i * GAP_MS));
    }
  };

  if (state === 'blocked' || state === 'unavailable') {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2" data-testid={`${testId}-${state}`}>
        <p className="font-semibold">{state === 'blocked' ? 'Camera is blocked — allow Camera for this site (lock icon next to the address), then try again.' : "The in-app camera isn't available on this device."}</p>
        <Button type="button" size="sm" onClick={() => setAttempt((a) => a + 1)} className="bg-amber-500 hover:bg-amber-600" data-testid={`${testId}-retry`}><RefreshCw className="w-4 h-4 mr-1" />Try again</Button>
      </div>
    );
  }
  return (
    <div className="rounded-2xl overflow-hidden bg-black relative" data-testid={testId}>
      <video ref={videoRef} playsInline muted autoPlay className="w-full max-h-[55vh] object-contain bg-black" style={facing === 'user' ? { transform: 'scaleX(-1)' } : undefined} data-testid={`${testId}-video`} />
      {state === 'starting' && <div className="absolute inset-0 flex items-center justify-center text-white text-sm"><Loader2 className="w-5 h-5 mr-2 animate-spin" />Opening camera…</div>}
      {turn && <TurnOverlay turn={turn} testId={testId} />}
      <div className="flex items-center justify-between gap-2 p-2 bg-black/80">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={!!turn} className="text-white hover:bg-white/10" data-testid={`${testId}-cancel`}><X className="w-4 h-4" /></Button>
        <Button type="button" onClick={capture} disabled={state !== 'live' || !!turn} className="h-12 px-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid={`${testId}-shoot`}>
          <Camera className="w-5 h-5 mr-2" />{turn ? 'Hold on…' : 'Capture'}
        </Button>
        <span className="w-9" />
      </div>
      {liveness && !turn && state === 'live' && <div className="px-3 pb-2 bg-black/80 text-xs text-amber-200 text-center" data-testid={`${testId}-turn-hint`}>Look straight, tap Capture, then turn your head when the arrow shows.</div>}
    </div>
  );
};

export default LiveSelfieCamera;
